import React, { useEffect, useState } from 'react';
import { useLang } from '../context/LanguageContext';
import { useUser } from '../context/UserContext';
import { getPayments, createPayment, getStudents, getCourses, chargeCard, createStandingOrder, createPaymentLink, getBillingHistory, createAccountingDoc, getAccountingDocPdf } from '../services/api';
import { exportTableToPDF } from '../utils/pdfExport';
import EmptyState from '../components/EmptyState';

const payStatusColors: Record<string, string> = { paid: 'badge-green', partial: 'badge-yellow', debt: 'badge-red' };

export default function Finance() {
  const { t } = useLang();
  const { userName } = useUser();
  const [payments, setPayments] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [showStandingOrderModal, setShowStandingOrderModal] = useState(false);
  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
  const [billingHistory, setBillingHistory] = useState<any[]>([]);
  const [generatedLink, setGeneratedLink] = useState('');
  const [receiptPrompt, setReceiptPrompt] = useState<{payment:any}|null>(null);
  const [lastReceipt, setLastReceipt] = useState<any>(null);
  const [form, setForm] = useState({
    studentId: '', courseId: '', amount: 0, paidAmount: 0,
    status: 'paid', method: 'bank_transfer', receiptNumber: '', date: '', notes: '',
    performedBy: ''
  });
  const [chargeForm, setChargeForm] = useState({
    studentId: '', amount: 0, installments: 1, description: '', performedBy: ''
  });
  const [standingOrderForm, setStandingOrderForm] = useState({
    studentId: '', monthlyAmount: 0, collectionDay: 1, numberOfMonths: 12,
    bankNumber: '', branchNumber: '', accountNumber: '', accountHolder: '', performedBy: ''
  });
  const [paymentLinkForm, setPaymentLinkForm] = useState({
    studentId: '', amount: 0, description: '', performedBy: ''
  });

  const loadData = () => {
    getPayments({ status: statusFilter || undefined }).then(res => setPayments(res.data)).catch(() => {});
    getStudents().then(res => setStudents(res.data)).catch(() => {});
    getCourses().then(res => setCourses(res.data)).catch(() => {});
    getBillingHistory().then(res => setBillingHistory(res.data)).catch(() => {});
  };

  useEffect(() => { loadData(); }, [statusFilter]);

  const formatPhoneForWhatsApp = (phone: string) => {
    const cleaned = phone.replace(/[^0-9]/g, '');
    return cleaned.startsWith('0') ? '972' + cleaned.slice(1) : '972' + cleaned;
  };

  const handleChargeCard = async () => {
    await chargeCard(chargeForm);
    setShowChargeModal(false);
    alert('התשלום בוצע בהצלחה!');
    loadData();
  };

  const handleCreateStandingOrder = async () => {
    await createStandingOrder(standingOrderForm);
    setShowStandingOrderModal(false);
    alert('הוראת הקבע הוקמה בהצלחה!');
    loadData();
  };

  const handleCreatePaymentLink = async () => {
    const res = await createPaymentLink(paymentLinkForm);
    const linkUrl = `${window.location.origin}/pay/${res.data.id}`;
    setGeneratedLink(linkUrl);
    loadData();
  };

  const handleAdd = async () => {
    const res = await createPayment(form);
    setShowModal(false);
    loadData();
    if (form.paidAmount > 0) {
      setReceiptPrompt({ payment: res.data });
    }
  };

  const handleCreateReceipt = async (payment: any) => {
    const student = students.find(s => s.id === payment.studentId);
    const amount = payment.paidAmount || payment.amount || 0;
    const beforeVat = Math.round(amount / 1.17);
    const vat = amount - beforeVat;
    const doc = {
      docType: 'receipt',
      date: payment.date || new Date().toISOString().split('T')[0],
      customerName: student ? `${student.firstName} ${student.lastName}` : '',
      customerEmail: student?.email || '',
      customerPhone: student?.phone || '',
      customerIdNumber: student?.nationalId || '',
      studentId: payment.studentId,
      paymentId: payment.id,
      courseId: payment.courseId,
      items: [{ description: getCourseName(payment.courseId) || t('receiptDoc'), quantity: 1, unitPrice: amount }],
      totalBeforeVat: beforeVat, vatAmount: vat, totalWithVat: amount,
      performedBy: userName,
    };
    const created = await createAccountingDoc(doc);
    setLastReceipt(created.data);
    setReceiptPrompt(null);
  };

  const handleViewPdf = async (id: string) => {
    const res = await getAccountingDocPdf(id);
    const url = URL.createObjectURL(res.data);
    window.open(url, '_blank');
  };

  const getStudentName = (id: string) => {
    const s = students.find(st => st.id === id);
    return s ? `${s.firstName} ${s.lastName}` : id;
  };

  const getCourseName = (id: string) => {
    const c = courses.find(cr => cr.id === id);
    return c ? c.name : id;
  };

  const totalRevenue = payments.reduce((sum, p) => sum + (p.paidAmount || 0), 0);
  const totalDebt = payments.reduce((sum, p) => sum + (p.amount - p.paidAmount), 0);

  return (
    <div>
      <div className="page-title">
        <span className="page-title-text">💰 {t('finance')}</span>
        <button className="btn btn-primary" onClick={() => { setForm({...form, performedBy: userName}); setShowModal(true); }}>+ {t('addPayment')}</button>
      </div>

      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="stat-card">
          <div className="icon">💵</div>
          <div className="value">{totalRevenue.toLocaleString()} ₪</div>
          <div className="label">{t('totalRevenue')}</div>
        </div>
        <div className="stat-card" style={{ borderRightColor: '#dc3545' }}>
          <div className="icon">📛</div>
          <div className="value" style={{ color: '#dc3545' }}>{totalDebt.toLocaleString()} ₪</div>
          <div className="label">{t('totalDebt')}</div>
        </div>
        <div className="stat-card" style={{ borderRightColor: '#81D4FA' }}>
          <div className="icon">📊</div>
          <div className="value" style={{ color: '#81D4FA' }}>{payments.length}</div>
          <div className="label">{t('payments')}</div>
        </div>
      </div>

      {/* Billing Action Buttons */}
      <div className="page-section" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button className="btn" style={{ background: '#4CAF50', color: '#fff', border: 'none' }}
          onClick={() => { setChargeForm({...chargeForm, performedBy: userName}); setShowChargeModal(true); }}>
          💳 {t('chargeCard')}
        </button>
        <button className="btn" style={{ background: '#1976D2', color: '#fff', border: 'none' }}
          onClick={() => { setStandingOrderForm({...standingOrderForm, performedBy: userName}); setShowStandingOrderModal(true); }}>
          🏦 {t('createStandingOrder')}
        </button>
        <button className="btn" style={{ background: '#FF9800', color: '#fff', border: 'none' }}
          onClick={() => { setPaymentLinkForm({...paymentLinkForm, performedBy: userName}); setShowPaymentLinkModal(true); }}>
          🔗 {t('sendPaymentLink')}
        </button>
      </div>

      <div className="page-section">
        <div className="toolbar">
          <select className="form-select" style={{ width: 'auto', minWidth: 150 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">{t('all')}</option>
            <option value="paid">{t('paid')}</option>
            <option value="partial">{t('partial')}</option>
            <option value="debt">{t('debt')}</option>
          </select>
          <button className="btn btn-outline">{t('exportExcel')} 📥</button>
          <button
            className="btn"
            style={{ backgroundColor: '#B71C1C', color: '#fff', border: 'none' }}
            onClick={() => {
              const headers = [t('receiptNumber'), t('students'), t('courses'), t('amount'), t('paidAmount'), 'חוב', t('status'), t('paymentMethod'), t('date')];
              const rows = payments.map(p => [
                p.receiptNumber || '-', getStudentName(p.studentId), getCourseName(p.courseId),
                `${p.amount.toLocaleString()} ₪`, `${p.paidAmount.toLocaleString()} ₪`,
                `${(p.amount - p.paidAmount).toLocaleString()} ₪`,
                p.status === 'paid' ? t('paid') : p.status === 'partial' ? t('partial') : t('debt'),
                p.method === 'bank_transfer' ? t('bankTransfer') : p.method === 'credit_card' ? t('creditCard') : p.method === 'cash' ? t('cash') : p.method === 'check' ? t('check') : p.method || '-',
                p.date || '-'
              ]);
              const date = new Date().toISOString().split('T')[0];
              exportTableToPDF(t('finance'), headers, rows, `finance_${date}.pdf`);
            }}
          >{t('exportPDF')} 📄</button>
        </div>

        <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('receiptNumber')}</th>
              <th>{t('students')}</th>
              <th>{t('courses')}</th>
              <th>{t('amount')}</th>
              <th>{t('paidAmount')}</th>
              <th>חוב</th>
              <th>{t('status')}</th>
              <th>{t('paymentMethod')}</th>
              <th>{t('date')}</th>
              <th>{t('performedBy')}</th>
            </tr>
          </thead>
          <tbody>
            {payments.map(p => (
              <tr key={p.id}>
                <td>{p.receiptNumber || '-'}</td>
                <td>{getStudentName(p.studentId)}</td>
                <td>{getCourseName(p.courseId)}</td>
                <td>{p.amount.toLocaleString()} ₪</td>
                <td>{p.paidAmount.toLocaleString()} ₪</td>
                <td style={{ color: (p.amount - p.paidAmount) > 0 ? '#dc3545' : '#7B1FA2', fontWeight: 600 }}>
                  {(p.amount - p.paidAmount).toLocaleString()} ₪
                </td>
                <td><span className={`badge ${payStatusColors[p.status]}`}>
                  {p.status === 'paid' ? t('paid') : p.status === 'partial' ? t('partial') : t('debt')}
                </span></td>
                <td>
                  {p.method === 'bank_transfer' ? t('bankTransfer') :
                   p.method === 'credit_card' ? t('creditCard') :
                   p.method === 'cash' ? t('cash') :
                   p.method === 'check' ? t('check') : p.method || '-'}
                </td>
                <td>{p.date || '-'}</td>
                <td>{p.performedBy || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {payments.length === 0 && (
          <EmptyState
            icon="💰"
            title={t('emptyPayments')}
            actionLabel={t('addPayment')}
            onAction={() => { setForm({...form, performedBy: userName}); setShowModal(true); }}
          />
        )}
      </div>

      {/* Billing History */}
      {billingHistory.length > 0 && (
        <div className="page-section">
          <h3 style={{ color: '#7B1FA2', marginBottom: 16 }}>📋 {t('billingHistory')}</h3>
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('transactionId')}</th>
                <th>{t('transactionType')}</th>
                <th>{t('students')}</th>
                <th>{t('amount')}</th>
                <th>{t('status')}</th>
                <th>{t('date')}</th>
              </tr>
            </thead>
            <tbody>
              {billingHistory.map(b => (
                <tr key={b.id}>
                  <td dir="ltr">{b.transactionId || '-'}</td>
                  <td><span className={`badge ${b.type === 'credit_card' ? 'badge-blue' : b.type === 'standing_order' ? 'badge-gold' : 'badge-gray'}`}>
                    {b.type === 'credit_card' ? '💳 ' + t('creditCard') : b.type === 'standing_order' ? '🏦 ' + t('bankTransfer') : '🔗 ' + t('sendPaymentLink')}
                  </span></td>
                  <td>{getStudentName(b.studentId)}</td>
                  <td>{(b.amount || b.monthlyAmount || 0).toLocaleString()} ₪</td>
                  <td><span className={`badge ${b.status === 'success' ? 'badge-green' : b.status === 'pending' ? 'badge-yellow' : 'badge-red'}`}>
                    {b.status === 'success' ? t('success') : b.status === 'pending' ? t('pending') : t('failed')}
                  </span></td>
                  <td>{new Date(b.createdAt).toLocaleDateString('he-IL')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t('addPayment')}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('students')}</label>
                <select className="form-select" value={form.studentId} onChange={e => setForm({...form, studentId: e.target.value})}>
                  <option value="">-- בחר --</option>
                  {students.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t('courses')}</label>
                <select className="form-select" value={form.courseId} onChange={e => setForm({...form, courseId: e.target.value})}>
                  <option value="">-- בחר --</option>
                  {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('amount')} (₪)</label>
                <input className="form-input" type="number" value={form.amount} onChange={e => setForm({...form, amount: parseInt(e.target.value)})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('paidAmount')} (₪)</label>
                <input className="form-input" type="number" value={form.paidAmount} onChange={e => setForm({...form, paidAmount: parseInt(e.target.value)})} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('paymentStatus')}</label>
                <select className="form-select" value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                  <option value="paid">{t('paid')}</option>
                  <option value="partial">{t('partial')}</option>
                  <option value="debt">{t('debt')}</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t('paymentMethod')}</label>
                <select className="form-select" value={form.method} onChange={e => setForm({...form, method: e.target.value})}>
                  <option value="bank_transfer">{t('bankTransfer')}</option>
                  <option value="credit_card">{t('creditCard')}</option>
                  <option value="cash">{t('cash')}</option>
                  <option value="check">{t('check')}</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('receiptNumber')}</label>
                <input className="form-input" value={form.receiptNumber} onChange={e => setForm({...form, receiptNumber: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('date')}</label>
                <input className="form-input" type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('notes')}</label>
              <textarea className="form-textarea" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('actionPerformer')}</label>
              <input className="form-input" value={form.performedBy} onChange={e => setForm({...form, performedBy: e.target.value})} />
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleAdd}>{t('save')}</button>
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Charge Card Modal */}
      {showChargeModal && (
        <div className="modal-overlay" onClick={() => setShowChargeModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">💳 {t('chargeCard')}</h3>
              <button className="modal-close" onClick={() => setShowChargeModal(false)}>×</button>
            </div>
            <div className="form-group">
              <label className="form-label">{t('students')}</label>
              <select className="form-select" value={chargeForm.studentId} onChange={e => setChargeForm({...chargeForm, studentId: e.target.value})}>
                <option value="">-- בחר --</option>
                {students.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('amount')} (₪)</label>
                <input className="form-input" type="number" value={chargeForm.amount} onChange={e => setChargeForm({...chargeForm, amount: parseInt(e.target.value) || 0})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('installments')}</label>
                <input className="form-input" type="number" min="1" max="36" value={chargeForm.installments} onChange={e => setChargeForm({...chargeForm, installments: parseInt(e.target.value) || 1})} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('description')}</label>
              <input className="form-input" value={chargeForm.description} onChange={e => setChargeForm({...chargeForm, description: e.target.value})} />
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleChargeCard}>💳 {t('chargeCard')}</button>
              <button className="btn btn-outline" onClick={() => setShowChargeModal(false)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Standing Order Modal */}
      {showStandingOrderModal && (
        <div className="modal-overlay" onClick={() => setShowStandingOrderModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">🏦 {t('createStandingOrder')}</h3>
              <button className="modal-close" onClick={() => setShowStandingOrderModal(false)}>×</button>
            </div>
            <div className="form-group">
              <label className="form-label">{t('students')}</label>
              <select className="form-select" value={standingOrderForm.studentId} onChange={e => setStandingOrderForm({...standingOrderForm, studentId: e.target.value})}>
                <option value="">-- בחר --</option>
                {students.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('monthlyAmount')} (₪)</label>
                <input className="form-input" type="number" value={standingOrderForm.monthlyAmount} onChange={e => setStandingOrderForm({...standingOrderForm, monthlyAmount: parseInt(e.target.value) || 0})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('collectionDay')}</label>
                <input className="form-input" type="number" min="1" max="28" value={standingOrderForm.collectionDay} onChange={e => setStandingOrderForm({...standingOrderForm, collectionDay: parseInt(e.target.value) || 1})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('numberOfMonths')}</label>
                <input className="form-input" type="number" min="1" value={standingOrderForm.numberOfMonths} onChange={e => setStandingOrderForm({...standingOrderForm, numberOfMonths: parseInt(e.target.value) || 1})} />
              </div>
            </div>
            <h4 style={{ color: '#7B1FA2', margin: '12px 0' }}>{t('bankAccount')}</h4>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('bankNumber')}</label>
                <input className="form-input" value={standingOrderForm.bankNumber} onChange={e => setStandingOrderForm({...standingOrderForm, bankNumber: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('branchNumber')}</label>
                <input className="form-input" value={standingOrderForm.branchNumber} onChange={e => setStandingOrderForm({...standingOrderForm, branchNumber: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('accountNumber')}</label>
                <input className="form-input" value={standingOrderForm.accountNumber} onChange={e => setStandingOrderForm({...standingOrderForm, accountNumber: e.target.value})} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('accountHolder')}</label>
              <input className="form-input" value={standingOrderForm.accountHolder} onChange={e => setStandingOrderForm({...standingOrderForm, accountHolder: e.target.value})} />
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleCreateStandingOrder}>🏦 {t('createStandingOrder')}</button>
              <button className="btn btn-outline" onClick={() => setShowStandingOrderModal(false)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Link Modal */}
      {showPaymentLinkModal && (
        <div className="modal-overlay" onClick={() => { setShowPaymentLinkModal(false); setGeneratedLink(''); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">🔗 {t('sendPaymentLink')}</h3>
              <button className="modal-close" onClick={() => { setShowPaymentLinkModal(false); setGeneratedLink(''); }}>×</button>
            </div>
            {!generatedLink ? (
              <>
                <div className="form-group">
                  <label className="form-label">{t('students')}</label>
                  <select className="form-select" value={paymentLinkForm.studentId} onChange={e => setPaymentLinkForm({...paymentLinkForm, studentId: e.target.value})}>
                    <option value="">-- בחר --</option>
                    {students.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{t('amount')} (₪)</label>
                  <input className="form-input" type="number" value={paymentLinkForm.amount} onChange={e => setPaymentLinkForm({...paymentLinkForm, amount: parseInt(e.target.value) || 0})} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('description')}</label>
                  <input className="form-input" value={paymentLinkForm.description} onChange={e => setPaymentLinkForm({...paymentLinkForm, description: e.target.value})} />
                </div>
                <div className="modal-actions">
                  <button className="btn btn-primary" onClick={handleCreatePaymentLink}>🔗 {t('sendPaymentLink')}</button>
                  <button className="btn btn-outline" onClick={() => setShowPaymentLinkModal(false)}>{t('cancel')}</button>
                </div>
              </>
            ) : (
              <div>
                <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 8, marginBottom: 16, wordBreak: 'break-all', direction: 'ltr' }}>
                  {generatedLink}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-primary" onClick={() => { navigator.clipboard.writeText(generatedLink); alert('הקישור הועתק!'); }}>
                    📋 {t('copyLink')}
                  </button>
                  <button className="btn btn-whatsapp" onClick={() => {
                    const student = students.find(s => s.id === paymentLinkForm.studentId);
                    if (student?.phone) {
                      const waPhone = formatPhoneForWhatsApp(student.phone);
                      const text = encodeURIComponent(`שלום ${student.firstName}, הנה קישור לתשלום: ${generatedLink}`);
                      window.open(`https://wa.me/${waPhone}?text=${text}`, '_blank');
                    }
                  }}>
                    📱 {t('sendViaWhatsApp')}
                  </button>
                  <button className="btn btn-email" onClick={() => {
                    const student = students.find(s => s.id === paymentLinkForm.studentId);
                    if (student?.email) {
                      window.open(`mailto:${student.email}?subject=קישור לתשלום&body=שלום ${student.firstName}, הנה קישור לתשלום: ${generatedLink}`, '_blank');
                    }
                  }}>
                    📧 {t('sendViaEmail')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Receipt Prompt Modal */}
      {receiptPrompt && (
        <div className="modal-overlay" onClick={() => setReceiptPrompt(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">🧾 {t('receiptDoc')}</h3>
              <button className="modal-close" onClick={() => setReceiptPrompt(null)}>×</button>
            </div>
            <p style={{ margin: '12px 0' }}>{t('createReceiptPrompt')}</p>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={() => handleCreateReceipt(receiptPrompt.payment)}>{t('createReceiptYes')}</button>
              <button className="btn btn-outline" onClick={() => setReceiptPrompt(null)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Created Modal */}
      {lastReceipt && (
        <div className="modal-overlay" onClick={() => setLastReceipt(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">🧾 {t('receiptDoc')}</h3>
              <button className="modal-close" onClick={() => setLastReceipt(null)}>×</button>
            </div>
            <p style={{ margin: '12px 0' }}>{t('receiptCreated')} {lastReceipt.docNumber}</p>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={() => handleViewPdf(lastReceipt.id)}>📄 {t('viewPdf')}</button>
              <button className="btn btn-outline" onClick={() => setLastReceipt(null)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
